import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pubApi, setPublisherToken } from '../../api/publisherClient';
import PublisherAuthLayout from './PublisherAuthLayout';
import Seo from '../../components/Seo';
import Icon from '../../components/Icon';

const PLATFORMS = ['youtube', 'tiktok', 'twitch', 'kick', 'instagram', 'other'];

function AvatarPicker({ file, onChange }) {
  const { t } = useTranslation();
  const preview = file ? URL.createObjectURL(file) : null;
  return (
    <div className="field">
      <label>{t('publisher.avatar_label')}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
          background: 'var(--bg-1)', border: '1px solid var(--border)', display: 'grid', placeItems: 'center',
        }}>
          {preview ? <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Icon name="users" size={22} style={{ color: 'var(--text-3)' }} />}
        </div>
        <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
          {file ? t('publisher.avatar_change') : t('publisher.avatar_upload')}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => onChange(e.target.files?.[0] || null)}
          />
        </label>
      </div>
      <div className="hint">{t('publisher.avatar_hint')}</div>
    </div>
  );
}

export default function PublisherRegister() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [fullName, setFullName] = useState('');
  const [platforms, setPlatforms] = useState([]);
  const [avatar, setAvatar] = useState(null);

  const togglePlatform = (p) =>
    setPlatforms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError(t('auth.err_weak_password'));
      return;
    }
    if (!platforms.length) {
      setError(t('publisher.err_platform_required'));
      return;
    }
    if (!avatar) {
      setError(t('publisher.err_avatar_required'));
      return;
    }
    setBusy(true);
    try {
      const data = await pubApi.register({ email, password, displayName, fullName: fullName || undefined, platforms });
      setPublisherToken(data.token);
      try {
        await pubApi.uploadAvatar(avatar);
      } catch (_) {
        // Kayıt zaten başarılı — fotoğraf panelden tekrar yüklenebilir.
      }
      navigate('/yayinci/panel', { replace: true });
    } catch (err) {
      setError(t([`publisher.err_${err.code}`, 'auth.err_generic']));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublisherAuthLayout>
      <Seo title="Yayıncı Kaydı" description="Ücretsiz yayıncı hesabı aç, sunucu sahibinden onay gerekmez. Kendi takip linkini üret, tıklamalarını gör." />
      <div className="auth-head">
        <h1>{t('publisher.register_title')}</h1>
        <p className="sub">{t('publisher.register_sub')}</p>
      </div>

      {error && (
        <div className="form-error">
          <Icon name="alert-triangle" size={15} style={{ marginTop: 1, flexShrink: 0 }} />
          {error}
        </div>
      )}

      <form onSubmit={submit} noValidate>
        <AvatarPicker file={avatar} onChange={setAvatar} />

        <div className="field">
          <label htmlFor="displayName">{t('publisher.channel_name')}</label>
          <div className="control">
            <input id="displayName" type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required maxLength={80} placeholder="Kürşat TV" />
          </div>
          <div className="hint">{t('publisher.channel_name_hint')}</div>
        </div>

        <div className="field">
          <label>{t('publisher.platform_label')}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {PLATFORMS.map((p) => {
              const on = platforms.includes(p);
              return (
                <button
                  type="button"
                  key={p}
                  onClick={() => togglePlatform(p)}
                  className={`btn btn-sm ${on ? '' : 'btn-ghost'}`}
                  aria-pressed={on}
                >
                  {on && <Icon name="check" size={12} />} {t(`publisher.platform_${p}`)}
                </button>
              );
            })}
          </div>
          <div className="hint">{t('publisher.platform_multi_hint')}</div>
        </div>

        <div className="field">
          <label htmlFor="fullName">{t('publisher.full_name')}</label>
          <div className="control">
            <input id="fullName" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} maxLength={120} placeholder={t('publisher.full_name_ph')} />
          </div>
          <div className="hint">{t('publisher.full_name_hint')}</div>
        </div>

        <div className="field">
          <label htmlFor="email">{t('common.email')}</label>
          <div className="control">
            <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" placeholder="sen@youtube.com" />
          </div>
        </div>
        <div className="field">
          <label htmlFor="password">{t('common.password')}</label>
          <div className="control">
            <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" placeholder="••••••••" />
          </div>
          <div className="hint">{t('auth.password_hint')}</div>
        </div>

        <button className="btn btn-block" disabled={busy}>
          {busy ? `${t('common.loading')}…` : t('publisher.register_action')}
        </button>
      </form>

      <div className="auth-alt">
        {t('publisher.have_account')} <Link to="/yayinci/giris">{t('publisher.login_action')}</Link>
      </div>
    </PublisherAuthLayout>
  );
}
